import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Tag } from 'antd';
import { observer } from 'mobx-react';

import EPTag from '@/comp/common/EPTag';
import { ImageTags } from '@/constants/CompressorImageTagData.const';
import {
  COMPRESSOR_IMAGE_TAG_DIRECTORY_DATA,
  getImageTagDataByDeviceType,
} from '@/constants/CompressorImageTagData.const';
import { useStores } from '@/hooks/use-stores';

import { StyledHeader } from './ImagePreviewTagHeader.styles';

type Props = {
  id: string;
  filename: string;
  tag: ImageTags | null;
  onEdit?: (tag: ImageTags) => void;
  currentlyEditing?: boolean;
  setCurrentlyEditing?: (show: boolean) => void;
  disabled?: boolean;
};

export const ImagePreviewTagHeader: FC<Props> = observer(
  ({ id, filename, tag, onEdit, currentlyEditing, setCurrentlyEditing, disabled }) => {
    const { t } = useTranslation();
    const [selectedTag, setSelectedTag] = useState<ImageTags>(tag);
    const { mapBuilderStore } = useStores();
    const handleEditClick = () => {
      setCurrentlyEditing?.(true);
    };

    const handleSubmit = async () => {
      if (selectedTag !== tag) {
        await onEdit?.(selectedTag);
      }

      setCurrentlyEditing?.(false);
    };

    useEffect(() => {
      setCurrentlyEditing?.(false);
      setSelectedTag(tag);
    }, [id, filename, tag]);

    return (
      <StyledHeader>
        <p className="image-preview-filename">{filename}</p>
        {tag && !onEdit && (
          <EPTag className="image-tag" label={t(COMPRESSOR_IMAGE_TAG_DIRECTORY_DATA[tag]?.translationKey)} />
        )}
        {onEdit &&
          (!currentlyEditing ? (
            <div className="image-tag-edit-container">
              <EPTag className="image-tag" label={t(COMPRESSOR_IMAGE_TAG_DIRECTORY_DATA[tag]?.translationKey)} />
              <Button className="preview-btn" type="text" size="large" onClick={handleEditClick} disabled={disabled}>
                {t('uploadImageEditButtonText')}
              </Button>
            </div>
          ) : (
            <div className="image-tag-edit-container image-tag-edit-container-editing">
              <div className="image-tag-list">
                {getImageTagDataByDeviceType(mapBuilderStore.selectedDevice?.deviceType).map(data => (
                  <Tag.CheckableTag
                    key={data.tag}
                    className="image-tag"
                    checked={data.tag === selectedTag}
                    onChange={() => setSelectedTag(data.tag)}
                  >
                    {t(data.translationKey)}
                  </Tag.CheckableTag>
                ))}
              </div>
              <Button className="preview-btn" type="text" size="large" onClick={handleSubmit}>
                {t('uploadImageEditSaveButtonText')}
              </Button>
            </div>
          ))}
      </StyledHeader>
    );
  },
);
